/**
 * One-pass scheduler — pure (graph, previousPlan, config) → (newPlan, order, conflicts).
 *
 * Phase C Day 4 — leaf + summary scheduling, NO translation yet. The summary
 * translation step (D-08/D-09) lands in Day 5 inside `summary_translation.ts`
 * and is invoked from here as a post-step for moveProjects:true.
 *
 * Algorithm per `04-` §16 single-pass:
 *   1. Toposort `graph.nodes` over scheduling edges (containment excluded).
 *      Cycles in the scheduling edges should already have been short-circuited
 *      by the planner; we treat their absence here as an invariant.
 *   2. For each node in topo order:
 *      - excluded (D-05 drag target or `auto_scheduling:false`) → copy anchor.
 *      - direction:
 *          forward  (scheduleFromEnd:false) → `resolveAsap`
 *          backward (scheduleFromEnd:true)  → `resolveAlap`
 *      - Summary nodes under moveProjects:true: after own resolution, the
 *        Day 5 step will call `applyTranslation` to translate descendants.
 *        Day 4 leaves this as a TODO marker.
 *   3. Output is a fresh `PlanState` independent of `previousPlan`. No
 *      mid-pass mutation of inputs (Part I §4 fix).
 */

import type { SchedulingConfig, NormalizedTaskID, ScheduleReason } from "../types";
import type { SchedulingGraph } from "../graph/types";
import { excludedFromMovableBound } from "../graph/scheduling_graph";
import { topologicalSortWithNodes } from "../utils/toposort";
import { resolveAsap } from "./asap";
import { resolveAlap } from "./alap";
import { newAccumulator, type ConflictAccumulator } from "./conflict";
import { applyTranslation, aggregateSummaryDates, clampDescendantsToSummaryBound } from "./summary_translation";

export interface PerNodePlan {
  readonly startTs: number;
  readonly endTs: number;
  readonly reason: ScheduleReason;
}

export type PlanState = ReadonlyMap<NormalizedTaskID, PerNodePlan>;

export interface PassInputs {
  readonly graph: SchedulingGraph;
  readonly previousPlan: PlanState;
  readonly config: SchedulingConfig;
  /** Optional component-scoping: nodes outside this set keep their previousPlan entry. */
  readonly scope?: ReadonlySet<NormalizedTaskID>;
  /**
   * Project anchors from the snapshot (per D-23). The forward path uses
   * `projectStartTs` as the implicit floor for tasks with no own anchor or
   * predecessor; the backward path uses `projectEndTs` as the terminal-node
   * latestEnd. Without these, ALAP scheduling falls back to per-node
   * anchorEndTs and never honors `gantt.config.project_end`.
   */
  readonly projectStartTs?: number;
  readonly projectEndTs?: number;

  readonly explicitProjectStartTs?: number;
  readonly explicitProjectEndTs?: number;

  /**
   * Optional normalized id of the task that initiated the autoSchedule call
   * (`gantt.autoSchedule(rootTaskId)`). v1's `preferInitialTaskDates` treats
   * this task as anchored to its current start_date even under
   * `gap_behavior: "compress"`, so partial reschedules don't drag the
   * caller's reference task to its earliest feasible position. Only consulted
   * under `apply_constraints: false`; constraint mode positions every task
   * purely by link-driven dates.
   */
  readonly rootTaskId?: NormalizedTaskID;

  /**
   * Strict predecessor-closure of `rootTaskId` (the tasks UPSTREAM of the
   * user-invoked task), per `connected.selectPredecessorClosure`. These nodes
   * are pulled into the inclusive scope only to BOUND the rescheduled task —
   * v1's drag handler never re-resolves a dragged task's predecessors. Under
   * `gap_behavior:"compress"` the pass would otherwise compress them to their
   * earliest feasible start (they're in scope); to match v1 they get the same
   * anchor-floor ("preserve") treatment as `rootTaskId`, so a predecessor the
   * user deliberately positioned stays put when a DOWNSTREAM task is dragged.
   * Like `rootTaskId`, only consulted under `apply_constraints: false`.
   * Inert under `gap_behavior:"preserve"` (the anchor floor is already the
   * default there). Empty/undefined for a full (unscoped) reschedule.
   */
  readonly rootPredecessorIds?: ReadonlySet<NormalizedTaskID>;
}

export interface PassResult {
  readonly plan: PlanState;
  readonly order: readonly NormalizedTaskID[];
  readonly conflicts: ConflictAccumulator;
}

/**
 * Effective (children-derived) bounds of a summary, read from the current
 * plan. `start = min(descendant.start)`, `end = max(descendant.end)` over the
 * summary's containment list. Mirrors `getSubtaskDates`, but against the
 * in-progress plan rather than the frozen snapshot, so internal-link pushes are
 * reflected before a cross-summary link reads the summary's end.
 *
 * A descendant with no plan entry uses its snapshot ANCHOR position. This is the
 * out-of-scope case in a SCOPED pass: when a deep-leaf drag pulls an ancestor
 * summary into scope only to RE-AGGREGATE (a `rootAncestorSummary`), the
 * GS-984 expansion deliberately leaves that summary's OTHER descendants out of
 * scope, so they have no plan entry. Skipping them would compute the summary's
 * start from only the in-scope subset — e.g. dragging child Task3 while sibling
 * Task2 (the earliest child, defining the project start) is out of scope made
 * the summary start track the dragged child, and its SS/SF successors followed
 * the wrong date (test 73 phase 1). The aggregate must span ALL descendants:
 * their planned position when resolved, else their current (anchor) position.
 * In a full (unscoped) pass every descendant has an entry, so this is inert.
 */
function summaryBounds(
  graph: SchedulingGraph,
  summaryId: NormalizedTaskID,
  plan: ReadonlyMap<NormalizedTaskID, PerNodePlan>,
  fallback: { anchorStartTs: number; anchorEndTs: number }
): { start: number; end: number } {
  let minStart = Number.POSITIVE_INFINITY;
  let maxEnd = Number.NEGATIVE_INFINITY;
  for (const c of graph.containment) {
    if (c.summary !== summaryId) continue;
    const entry = plan.get(c.descendant);
    let s: number;
    let e: number;
    if (entry) {
      s = entry.startTs;
      e = entry.endTs;
    } else {
      const node = graph.nodes.get(c.descendant);
      if (!node) continue;
      s = node.anchorStartTs;
      e = node.anchorEndTs;
    }
    if (s < minStart) minStart = s;
    if (e > maxEnd) maxEnd = e;
  }
  if (!Number.isFinite(minStart) || !Number.isFinite(maxEnd)) {
    return { start: fallback.anchorStartTs, end: fallback.anchorEndTs };
  }
  return { start: minStart, end: maxEnd };
}

// `excludedFromMovableBound` — the constraint-pinned-leaf predicate shared with
// the graph builder's project-shape offset baseline — lives in
// `../graph/scheduling_graph` (SchedNode's home module; planner→graph is the
// established import direction).

/**
 * The block's movable bound: `min(leaf start)` (forward) / `max(leaf end)`
 * (backward) over LEAF descendants that free-flow with the block (i.e. not
 * `excludedFromMovableBound`). `graph.containment` is TRANSITIVE, so a
 * multi-level summary sees its grandchildren directly — and only the leaves
 * count, because an intermediate summary's start is a derived aggregate that
 * may itself be held by a constraint-pinned grandchild (those grandchildren are
 * judged directly here). Returns `undefined` when every leaf is pinned (caller
 * falls back to the full children-derived bound).
 *
 * GS-2875: a leaf positioned by its OWN scheduling link — an incoming edge
 * under forward, an outgoing edge under backward — does not free-flow with the
 * block either: it is tethered to its predecessor (successor), so it can't
 * define the front the block's resolve anchor / delta are measured from. The
 * case where this matters is a NEGATIVE-lag internal link whose tail sticks
 * out BEFORE the chain head (`3FS-6` with |lag| > duration): measuring the
 * delta from the tail dragged the whole block — including the tail's own
 * in-project predecessor — past the link landing (Task #3 02-04 → 07-04 for a
 * one-day push), instead of pushing the chain head to the landing and letting
 * the tail re-derive from it. Mirrors v1's GS-2427 convention
 * (`links_common.js` `_formatLink` targetDates: `!child.$target.length` — the
 * summary-target link anchor is the earliest child WITHOUT an own incoming
 * link; symmetric `!c.$source.length` on the backward/source side). For lag ≥ 0
 * chains this is inert: a link-driven leaf resolves at/after its in-block
 * predecessor, so it never defines the front anyway. Falls back to the
 * link-driven leaves (previous behavior) when every free leaf is link-tethered.
 *
 * `mode` (GS-2875 follow-up — SNET-pinned head): a block PULL (the landing
 * sits before the current front under forward; after it under backward) may
 * only be absorbed by leaves that can actually move TOWARD the landing, so
 * the constraint-kind exclusion flips to the OPPOSITE direction's list (a
 * pull-back is blocked by SNET/FNET/MSO/MFO, not by FNLT/SNLT). Tethered
 * leaves never absorb a pull either — their dates are link-derived, and
 * dragging them breaks the very links the per-leaf resolve just honored
 * (Task #4 = #3.end − 6 was dragged to the landing while option A clamped
 * its SNET predecessor in place). There is NO link-driven fallback tier in
 * pull mode: when nothing can move, the caller forces delta = 0 instead.
 */
function movableBound(
  graph: SchedulingGraph,
  summaryId: NormalizedTaskID,
  plan: ReadonlyMap<NormalizedTaskID, PerNodePlan>,
  direction: "forward" | "backward",
  mode: "push" | "pull" = "push"
): number | undefined {
  const forward = direction === "forward";
  // Which constraint kinds pin a leaf against THIS move: for a push the
  // landing-ward direction is the pass direction; for a pull it's the
  // opposite one.
  const constraintDirection: "forward" | "backward" = mode === "push"
    ? direction
    : (forward ? "backward" : "forward");
  let bestFree = forward ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
  let bestAny = bestFree;
  let foundFree = false;
  let foundAny = false;
  for (const c of graph.containment) {
    if (c.summary !== summaryId) continue;
    const node = graph.nodes.get(c.descendant);
    if (!node || node.kind === "summary") continue;
    if (excludedFromMovableBound(node, constraintDirection)) continue;
    // Same out-of-scope handling as `summaryBounds`: a re-aggregating summary's
    // out-of-scope leaf still defines the movable head/tail at its current
    // (anchor) position. Without this the movable bound (and hence the
    // summary's resolve anchor) would track only the in-scope subset (test 73
    // phase 1). In a full pass every leaf has a plan entry, so this is inert.
    const entry = plan.get(c.descendant);
    const startTs = entry ? entry.startTs : node.anchorStartTs;
    const endTs = entry ? entry.endTs : node.anchorEndTs;
    const bound = forward ? startTs : endTs;
    foundAny = true;
    if (forward ? bound < bestAny : bound > bestAny) bestAny = bound;
    const ownLinks = forward
      ? graph.predecessors.get(c.descendant)
      : graph.successors.get(c.descendant);
    if (ownLinks && ownLinks.length > 0) continue; // link-tethered, see GS-2875 above
    foundFree = true;
    if (forward ? bound < bestFree : bound > bestFree) bestFree = bound;
  }
  if (foundFree) return bestFree;
  if (mode === "pull") return undefined; // tethered leaves never absorb a pull
  return foundAny ? bestAny : undefined;
}

export function scheduleOnce(inputs: PassInputs): PassResult {
  const { graph, previousPlan, config, scope, explicitProjectStartTs, explicitProjectEndTs, rootTaskId, rootPredecessorIds } = inputs;

  // Build the toposort over scheduling edges only (containment is not a
  // scheduling edge). Restrict to in-scope nodes if a scope is provided.
  const nodeIds: NormalizedTaskID[] = [];
  for (const id of graph.nodes.keys()) {
    if (scope && !scope.has(id)) continue;
    nodeIds.push(id);
  }
  const edgesForToposort: { source: NormalizedTaskID; target: NormalizedTaskID }[] = [];
  for (const e of graph.edges) {
    if (scope && (!scope.has(e.source) || !scope.has(e.target))) continue;
    edgesForToposort.push({ source: e.source, target: e.target });
  }
  // Under moveProjects, a summary's scheduled position is derived from its
  // descendants (its effective span = min/max of their planned dates), and a
  // cross-summary link reads `predEnds[summary]`. So descendants MUST be
  // resolved before their summary. Containment isn't a scheduling edge, so we
  // add it as an ordering-only constraint. Parent-child scheduling links are
  // filtered out at snapshot build (D-25) for summaries, so this can't
  // introduce a cycle with the scheduling edges.
  //
  // Direction-aware: the resolver iterates `order` forward under
  // `scheduleFromEnd:false` and REVERSED under `scheduleFromEnd:true`. To
  // keep "descendants before summary" in BOTH iteration orders we flip the
  // containment edge direction per scheduling direction — under forward the
  // edge is `descendant → summary` (descendants topologically before the
  // summary, processed first); under backward the edge is `summary →
  // descendant` (summary topologically before the descendants, so the
  // reversed iter still visits descendants first). Without this flip, the
  // backward path used to process the summary BEFORE its descendants and
  // fell back to the stale snapshot anchor for `summaryBounds`, producing a
  // spurious translation delta (test 054 step 1 GS-backward project: tasks
  // 17 / 19 dragged to ~17-04 instead of staying near projectEnd).
  const containmentDirForward = !config.scheduleFromEnd;
  if (config.moveProjects) {
    for (const c of graph.containment) {
      if (scope && (!scope.has(c.descendant) || !scope.has(c.summary))) continue;
      edgesForToposort.push(containmentDirForward
        ? { source: c.descendant, target: c.summary }
        : { source: c.summary, target: c.descendant });
    }
  }
  const order = topologicalSortWithNodes(nodeIds, edgesForToposort);
  if (!order) {
    throw new Error("schedule_pass: cycles in scheduling graph (should be short-circuited by planner)");
  }

  const plan = new Map<NormalizedTaskID, PerNodePlan>();
  const conflicts = newAccumulator();

  // Seed plan with out-of-scope previousPlan entries so successors that
  // reach back across the scope boundary still find ends/starts to use.
  if (scope) {
    for (const [id, entry] of previousPlan) {
      if (!scope.has(id)) plan.set(id, entry);
    }
  }

  // Helper accessors for asap/alap.
  const predEnds = new Map<NormalizedTaskID, number>();
  const succStarts = new Map<NormalizedTaskID, number>();

  const direction: "forward" | "backward" = config.scheduleFromEnd ? "backward" : "forward";

  // Forward path scans toposort low→high; backward scans high→low.
  const iter = direction === "forward" ? order : [...order].reverse();

  // D-08 claim-tracking: once a summary translates its descendants, those
  // descendants must NOT be re-resolved by their own asap/alap (which would
  // overwrite the translation back to anchor). The summary "claims" them.
  // Containment is non-toposorted so iter order doesn't guarantee summary
  // last; claim-tracking gives deterministic behavior regardless of order.
  const claimed = new Set<NormalizedTaskID>();

  for (const id of iter) {
    if (claimed.has(id)) continue;
    const node = graph.nodes.get(id);
    if (!node) continue;

    // D-05 excluded OR D-12 completed (Phase D Day 9 perf): copy anchor,
    // skip the resolveAsap/Alap call. Saves O(V) calendar ops at scale.
    if (node.excluded || node.isCompleted) {
      const entry: PerNodePlan = {
        startTs: node.anchorStartTs,
        endTs: node.anchorEndTs,
        reason: { kind: "preferred" }
      };
      plan.set(id, entry);
      predEnds.set(id, entry.endTs);
      succStarts.set(id, entry.startTs);
      continue;
    }

    // Under moveProjects, a summary is scheduled as a block: its effective
    // span comes from its already-resolved descendants (NOT the frozen
    // snapshot anchor span, which is stale once internal links push the
    // children), and the whole block translates to honor any cross-summary
    // link. We feed the children-derived bounds to the resolver via an
    // "effective" node so all the edge / constraint / project-floor logic is
    // reused, then translate descendants by the resulting delta.
    const isMovingSummary = node.kind === "summary" && config.moveProjects;
    // Effective bounds for the summary's resolve. The reference endpoint (START
    // forward / END backward) uses the MOVABLE bound — the min/max over leaves
    // that free-flow with the block — instead of `min/max(all children)`. A
    // constraint-pinned-early leaf (e.g. an MSO/MFO/FNLT grandchild) otherwise
    // drags the reference behind the movable head, so a no-link inner summary
    // resolves to a position ≠ its movable anchor and computes a spurious
    // delta, which then double-translates its leaves under a moving parent
    // (the multi-level drift in GS-1771 / retest 087). The opposite endpoint
    // keeps the full children-derived bound so the summary's span (and hence
    // `predEnds[summary]` for cross-summary successors) still covers every
    // descendant. `aggregateSummaryDates` re-derives the final span at
    // end-of-pass regardless.
    const fullBounds = isMovingSummary ? summaryBounds(graph, id, plan, node) : null;
    let effBounds = fullBounds;
    if (isMovingSummary && fullBounds) {
      const mov = movableBound(graph, id, plan, direction);
      if (mov !== undefined) {
        effBounds = direction === "forward"
          ? { start: mov, end: fullBounds.end }
          : { start: fullBounds.start, end: mov };
      }
    }
    const resolveNode = effBounds
      ? { ...node, anchorStartTs: effBounds.start, anchorEndTs: effBounds.end }
      : node;

    // v1 `preferInitialTaskDates` parity: under `apply_constraints:false`,
    // the user-invoked start task carries its current start_date as a
    // preferred floor even when `gap_behavior:"compress"` would otherwise
    // pull every node to its earliest feasible date. Implementation: hand
    // `resolveAsap`/`resolveAlap` a "preserve" gapBehavior for this node so
    // the anchor-floor branch kicks in. Predecessors can still push the
    // task LATER; only the compress pull is suppressed. Constraint mode
    // (`apply_constraints:true`) is link-driven by design and skips this
    // override — matches the v1 helper's early return.
    //
    // The same anchor-floor applies to the root's PREDECESSORS (the upstream
    // tasks pulled into the inclusive scope). v1 collects them only to bound
    // the dragged task and never re-resolves them; under compress v2 would
    // otherwise compress a predecessor the user deliberately positioned when a
    // DOWNSTREAM task is dragged (retest 030: dragging task 22 backward pulled
    // its predecessor 18 from 11-04 to 10-04). Giving them the preserve floor
    // keeps them at their current date while still letting THEIR own
    // predecessors push them later. Inert under preserve (already the default).
    const useAsRootAnchor =
      !config.applyConstraints &&
      rootTaskId !== undefined &&
      (id === rootTaskId || rootPredecessorIds?.has(id) === true);
    const effectiveGapBehavior: "preserve" | "compress" = useAsRootAnchor
      ? "preserve"
      : config.gapBehavior;

    let resolved: PerNodePlan;
    if (direction === "forward") {
      const preds = graph.predecessors.get(id) ?? [];
      const r = resolveAsap({
        node: resolveNode,
        predecessorEdges: preds,
        predEnds,
        // `succStarts` accumulates each resolved node's start (see below), so
        // for a predecessor it holds the source's planned start — exactly what
        // SS/SF edges need to anchor on a summary's real start, not
        // `end − duration`.
        predStarts: succStarts,
        applyConstraints: config.applyConstraints,
        projectStartTs: explicitProjectStartTs,
        gapBehavior: effectiveGapBehavior
      });
      resolved = r;
    } else {
      const succs = graph.successors.get(id) ?? [];
      const r = resolveAlap({
        node: resolveNode,
        successorEdges: succs,
        succStarts,
        applyConstraints: config.applyConstraints,
        projectEndTs: explicitProjectEndTs,
        gapBehavior: effectiveGapBehavior
      });
      resolved = r;
    }

    // GS-2485 (leaf-calendar-aware FS link target). v1 expands a project→
    // project FS link to leaf-to-leaf implicit links and snaps each link's
    // candidate in the LEAF's calendar (`ext/auto_scheduling/asap_strategy.ts`
    // no-lag branch: `getClosestWorkTime(predEnd, future, task: successor)`).
    // A leaf whose own calendar has a non-working interval at the predecessor
    // end (e.g. lunch break in the global 8-12+13-17 default) can't legally
    // start at that moment — its snapped start is later, raising the summary's
    // effective earliest start. v2's block model targets the link at the
    // summary's calendar only, which can drag a summary earlier than its
    // leaves can legally start. Approximate v1 here for the FS-lag-0 case
    // (the case exercised by GS-2485): snap the source end in every leaf
    // descendant's calendar and take the max. The result can only RAISE
    // `resolved.startTs`, so it never produces drift; for flat / same-calendar
    // leaves the snap is a no-op and earlier multi-level fixes are
    // unaffected. FF/SS/SF and non-zero-lag are NOT covered — they need v1's
    // leaf-link expansion machinery, which is a separate redesign.
    if (isMovingSummary && direction === "forward") {
      let leafSnapped = Number.NEGATIVE_INFINITY;
      const preds = graph.predecessors.get(id) ?? [];
      for (const e of preds) {
        if (e.originalType !== "fs" || e.lag !== 0) continue;
        const predEnd = predEnds.get(e.source);
        if (predEnd === undefined) continue;
        for (const c of graph.containment) {
          if (c.summary !== id) continue;
          const leaf = graph.nodes.get(c.descendant);
          if (!leaf || leaf.kind === "summary") continue;
          const snapped = leaf.calendar
            .snapToWorking(new Date(predEnd), "forward")
            .getTime();
          if (snapped > leafSnapped) leafSnapped = snapped;
        }
      }
      if (Number.isFinite(leafSnapped) && leafSnapped > resolved.startTs) {
        const span = resolved.endTs - resolved.startTs;
        resolved = {
          startTs: leafSnapped,
          endTs: leafSnapped + span,
          reason: resolved.reason
        };
      }
    }

    plan.set(id, resolved);
    predEnds.set(id, resolved.endTs);
    succStarts.set(id, resolved.startTs);

    // D-08 / D-09: when a summary node resolves under moveProjects:true,
    // translate every descendant by delta. Hard rule — descendant
    // constraints surface as Conflict but the translated date is still
    // committed.
    //
    // The descendants are then "claimed" so subsequent iter entries don't
    // re-resolve them via their own asap/alap. (With containment added to the
    // toposort, descendants are normally resolved BEFORE their summary, so the
    // claim is belt-and-suspenders.)
    //
    // delta is measured from the summary's CURRENT (children-derived)
    // position, so descendants shift from the link-respecting dates they
    // already hold — preserving internal ordering across the move. Computing
    // delta from the frozen anchor instead was the GS-1689 + GS-2427 bug.
    if (isMovingSummary && effBounds) {
      const newAnchorTs = direction === "forward" ? resolved.startTs : resolved.endTs;
      // Delta anchor = the summary's MOVABLE reference endpoint (already baked
      // into `effBounds` above), NOT `min/max(all descendants)`. This keeps the
      // resolve anchor and the delta consistent, so:
      //   - a no-link inner summary resolves to its movable bound ⇒ delta 0 ⇒
      //     it doesn't independently move (and so doesn't double-translate its
      //     leaves under a moving parent — the multi-level drift, retest 087);
      //   - once a moved block's free head aligns with the link, `anchor ===
      //     resolved` ⇒ delta 0 ⇒ stable across repeat autoSchedule (sample 19);
      //   - a constraint-pinned-early leaf can't drag the anchor behind the
      //     movable head.
      let oldAnchorTs = direction === "forward" ? effBounds.start : effBounds.end;
      // PULL detection (GS-2875 follow-up): the landing sits BEFORE the front
      // (forward; after it under backward), i.e. constraint-mode strictness
      // wants the block back at its link landing. The pull may only be
      // absorbed by leaves movable TOWARD the landing — `movableBound` in
      // "pull" mode flips the constraint-kind exclusion (SNET/FNET pin a
      // pull-back the way FNLT/SNLT pin a push) and never falls back to
      // link-tethered leaves. When nothing can move (e.g. the chain head is
      // SNET-pinned and the only other leaf hangs off it via a negative-lag
      // link), the block stays put: delta 0, every leaf keeps its per-leaf
      // resolved date (Task #3 = SNET 11-04, Task #4 = #3.end − 6 = 07-04 —
      // previously Task #4 was dragged to the landing 03-04 on EVERY call
      // while option A clamped its predecessor back, an unconverging,
      // link-violating state). If the pull-movable bound already sits at or
      // before the landing, there is nothing to pull either — a pull must
      // never turn into a push.
      const pulling = direction === "forward" ? newAnchorTs < oldAnchorTs : newAnchorTs > oldAnchorTs;
      if (pulling) {
        const pullBound = movableBound(graph, id, plan, direction, "pull");
        if (
          pullBound === undefined ||
          (direction === "forward" ? pullBound <= newAnchorTs : pullBound >= newAnchorTs)
        ) {
          oldAnchorTs = newAnchorTs; // nothing can move toward the landing ⇒ delta 0
        } else {
          oldAnchorTs = pullBound;
        }
      }
      applyTranslation({
        graph,
        summaryId: id,
        oldAnchorTs,
        newAnchorTs,
        direction,
        plan,
        conflicts,
        strictCalendar: config.strictCalendar
      });
      for (const c of graph.containment) {
        if (c.summary === id) claimed.add(c.descendant);
      }

      // GS-2875 landing floor: the summary's link landing bounds every leaf
      // of the block (v1 explode parity — each leaf would carry its own
      // implicit edge from the link source, so `leaf = max(landing,
      // own-predecessor-derived date)`). A negative-lag tail tethered to an
      // in-block predecessor can otherwise sit before the landing — honoring
      // its internal link but violating the user-visible summary link
      // (Task #4 at 30-03 while Task #2, the project's FS predecessor, ends
      // 03-04). Gated on an EDGE actually driving the resolve (reason kind
      // asap/alap): when the anchor floor or a constraint won instead, the
      // resolved date is not a link landing and must not become a bound for
      // the leaves. Runs regardless of the translation delta — a delta-0
      // pass still re-derives tails from their in-block predecessors with no
      // knowledge of the summary-level link.
      if (resolved.reason.kind === "asap" || resolved.reason.kind === "alap") {
        clampDescendantsToSummaryBound(graph, id, newAnchorTs, direction, plan);
      }

      // Re-derive the summary's own mid-pass entry from the descendants'
      // POST-translation entries. The resolver's summary branch carried the
      // block's OLD wall-clock span (`endTs = startTs + (anchorEnd −
      // anchorStart)`), which over/under-counts non-working time whenever the
      // block's new position crosses a different number of weekend days than
      // its old one — e.g. a Thu→Wed block (one weekend inside) moved to a
      // Monday start (no weekend inside its working span) carried a 6-day
      // wall-clock span and parked `predEnds[summary]` on Sunday. Downstream
      // readers resolved DURING the pass (an FS milestone / leaf successor of
      // the summary) then schedule from that phantom weekend end and land
      // days later than a follow-up full autoSchedule() computes (sample 19:
      // drag Task #2.1 forward → Final milestone Mon Apr 8 via dnd pass vs
      // Fri Apr 5 via full pass). `aggregateSummaryDates` already fixes the
      // PLAN entry at end-of-pass, but `predEnds`/`succStarts` reads happen
      // mid-pass — so align them here with the same children-derived bbox.
      // The full (not movable) bounds are used, matching the end-of-pass
      // aggregate and the SS/SF "anchor on earliest child" rule; the fallback
      // keeps the resolver's result for a childless summary.
      const postBounds = summaryBounds(graph, id, plan, {
        anchorStartTs: resolved.startTs,
        anchorEndTs: resolved.endTs
      });
      const summaryEntry: PerNodePlan = {
        startTs: postBounds.start,
        endTs: postBounds.end,
        reason: resolved.reason
      };
      plan.set(id, summaryEntry);
      predEnds.set(id, summaryEntry.endTs);
      succStarts.set(id, summaryEntry.startTs);
    }
  }

  // v1 parity (`planner.iterateTasks` secondary loop): tasks with explicit
  // `constraint_type` ALAP under forward scheduling — or ASAP under backward
  // scheduling — are re-resolved against the opposite direction in a second
  // pass. v1 calls this the "secondary sequence" (`planner.ts:146-161`).
  // The main pass already wrote an asap/alap result; the secondary pass
  // overwrites it using the newly-resolved opposite-side neighbours. Run in
  // REVERSE iteration order so successors (forward) / predecessors
  // (backward) are already settled.
  //
  // Test 053 step 3 GS-`asap_tasks_to_project_start_linked_task`: task 22
  // got `constraint_type: "alap"` after the initial schedule. Under forward
  // scheduling its main-pass asap result anchored on `predEnds` (compressed
  // to project_start), but v1 places it at `successor.start − duration`
  // because of the ALAP type.
  const secondaryAlapType: "alap" | "asap" = direction === "forward" ? "alap" : "asap";
  for (let i = iter.length - 1; i >= 0; i--) {
    const id = iter[i];
    if (claimed.has(id)) continue;
    const node = graph.nodes.get(id);
    if (!node) continue;
    if (node.excluded || node.isCompleted) continue;
    if (node.kind === "summary") continue; // summary movement handled above
    if (node.constraintType !== secondaryAlapType) continue;

    let resolvedSecondary: PerNodePlan | undefined;
    if (secondaryAlapType === "alap") {
      const succs = graph.successors.get(id) ?? [];
      if (succs.length === 0) continue; // no successor → keep asap result
      resolvedSecondary = resolveAlap({
        node,
        successorEdges: succs,
        succStarts,
        applyConstraints: config.applyConstraints,
        projectEndTs: explicitProjectEndTs,
        gapBehavior: config.gapBehavior
      });
    } else {
      const preds = graph.predecessors.get(id) ?? [];
      if (preds.length === 0) continue; // no predecessor → keep alap result
      resolvedSecondary = resolveAsap({
        node,
        predecessorEdges: preds,
        predEnds,
        predStarts: succStarts,
        applyConstraints: config.applyConstraints,
        projectStartTs: explicitProjectStartTs,
        gapBehavior: config.gapBehavior
      });
    }
    plan.set(id, resolvedSecondary);
    predEnds.set(id, resolvedSecondary.endTs);
    succStarts.set(id, resolvedSecondary.startTs);
  }

  // D-07: derive summary dates from descendant plans after all scheduling
  // is done. Mutates `plan` in place.
  if (config.moveProjects) {
    aggregateSummaryDates(graph, plan);
  }

  // Return the order tasks were RESOLVED in, not the raw toposort. v1's
  // `planner.applyProjectPlan` iterates the result of `iterateTasks` in
  // iteration order, and downstream callers (the applier + the
  // `onAfterTaskUpdate` chain that fires for each mutation) expose this
  // ordering to user-supplied listeners. Test
  // `11_auto_scheduling/71_order_of_task_update_events_after_dragging_a_task_backward`
  // asserts the per-update event order is reverse-toposort under backward
  // scheduling — which is exactly `iter`, not `order`.
  return { plan, order: iter, conflicts };
}
